import type { PropsWithChildren } from 'react'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { ItemAction } from '@jotta/grpc-js-client/fileService'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import type { FileActionCallback } from '@jotta/types/FileActions'
import type { PathItemObject } from '@jotta/types/Files'
import type { FileRouteContext } from '@jotta/types/schemas/FileRouteSchema'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Headline2 } from '@jotta/ui/Headline'
import SvgAddFolder from '@jotta/ui/SvgAddFolder'
import SvgFileUpload from '@jotta/ui/SvgFileUpload'
import SvgFolderSimple from '@jotta/ui/SvgFolderSimple'
import SvgTrash from '@jotta/ui/SvgTrash'

import type { FileActionStore } from '../FileActions/FileActionStore'

import styles from './EmptyFolder.module.scss'

function getTitle(context: FileRouteContext): {
  title: string
  icon: BrandThemeIcon
} {
  switch (context) {
    case 'trash':
      return { title: t`Your trash can is empty`, icon: SvgTrash }

    default:
      return {
        title: t`It looks like this folder is empty`,
        icon: SvgFolderSimple,
      }
  }
}
export const EmptyFolder = observer<
  PropsWithChildren<{
    context?: FileRouteContext
    pathItem?: PathItemObject | null
    fileActionCallback?: FileActionCallback
    onUploadFiles?: () => void
    actions?: FileActionStore
  }>
>(function EmptyFolder({
  pathItem,
  context = 'sync',
  children,
  actions,
  fileActionCallback,
  onUploadFiles,
}) {
  const { title, icon } = getTitle(context)
  const { dispatch } = actions || {}
  const isTrash = context === 'trash'
  const showLoggedInFolderActions =
    !isTrash && pathItem && pathItem.actionList.includes(ItemAction.ADD_CHILD)

  const showPublicFolderActions = Boolean(
    !showLoggedInFolderActions &&
    pathItem &&
    pathItem.actionList.includes(ItemAction.ADD_CHILD),
  )
  return (
    <div data-testid="EmptyFolder" className={styles.grid}>
      <BrandIcon icon={icon} className={styles.icon} />
      <Headline2 className={styles.title}>{title}</Headline2>
      {children}
      {dispatch && actions && onUploadFiles && showLoggedInFolderActions && (
        <p className={styles.paragraph}>
          <Trans>
            To get started drop a file into this window,
            <br />
            choose a file to{' '}
            <Button
              className="btn btn-link"
              icon={SvgFileUpload}
              onClick={onUploadFiles}
            >
              Upload
            </Button>{' '}
            or{' '}
            <Button
              className="btn btn-link"
              icon={SvgAddFolder}
              onClick={() =>
                dispatch('CreateFolder', 'FOLDER_LIST', pathItem.path)
              }
            >
              Add a new folder
            </Button>
          </Trans>
        </p>
      )}
      {fileActionCallback && showPublicFolderActions && (
        <p className={styles.paragraph}>
          <Trans>
            To get started drop a file into this window,
            <br />
            choose a file to{' '}
            <Button
              className="btn btn-link"
              icon={SvgFileUpload}
              onClick={() => fileActionCallback('UPLOAD_FILES')}
            >
              Upload
            </Button>{' '}
            or{' '}
            <Button
              className="btn btn-link"
              icon={SvgAddFolder}
              onClick={() => fileActionCallback('UPLOAD_FOLDER')}
            >
              Add a new folder
            </Button>
          </Trans>
        </p>
      )}
    </div>
  )
})
